/*
 * Leitura do que a pessoa digita no campo de hora (TimePicker).
 * Hora sai em "HH:MM"; '' = campo vazio.
 */

export type Parsed = { ok: true; value: string } | { ok: false; error: string }

const pad = (n: number) => String(n).padStart(2, '0')

/**
 * Máscara enquanto digita: só números, os dois-pontos entram sozinhos.
 * "1745" → "17:45"; "930" → "09:30" (hora de 3 a 9 já fecha com zero);
 * "25" → "02:5"; minuto começando acima de 5 vira "0x". Apagando, não recoloca o ":".
 */
export function maskTimeInput(text: string, deleting = false): string {
  const d = text.replace(/\D/g, '')
  if (!d) return ''
  let hour: string
  let rest: string
  if (d[0]! >= '3') [hour, rest] = [`0${d[0]}`, d.slice(1)]
  else if (d.length === 1) return d
  else if (d[0] === '2' && d[1]! > '3') [hour, rest] = ['02', d.slice(1)]
  else [hour, rest] = [d.slice(0, 2), d.slice(2)]
  rest = rest.slice(0, 2)
  if (rest && rest[0]! > '5') rest = `0${rest[0]}`
  if (!rest) return deleting ? hour : `${hour}:`
  return `${hour}:${rest}`
}

const TIME_DIGITS = /^(\d{1,4})$/
const TIME_SEPARATED = /^(\d{1,2})\s*(?::|h|\.)\s*(\d{1,2})?\s*(?:min|m)?$/

/** "930" → 09:30, "1745" → 17:45, "9" → 09:00, "9h30"/"9:30" → 09:30, "9h" → 09:00. */
export function parseTimeInput(text: string): Parsed {
  const t = text.trim().toLowerCase()
  if (!t) return { ok: true, value: '' }

  let h: number
  let m: number
  const digits = TIME_DIGITS.exec(t)
  const separated = TIME_SEPARATED.exec(t)
  if (digits) {
    const d = digits[1]!
    if (d.length <= 2) [h, m] = [Number(d), 0]
    else [h, m] = [Number(d.slice(0, -2)), Number(d.slice(-2))]
  } else if (separated) {
    ;[h, m] = [Number(separated[1]), Number(separated[2] ?? 0)]
  } else {
    return { ok: false, error: 'Hora não reconhecida. Digite 930, 9:30 ou 9h30.' }
  }
  if (h > 23) return { ok: false, error: 'Hora vai de 00 a 23.' }
  if (m > 59) return { ok: false, error: 'Minutos vão de 00 a 59.' }
  return { ok: true, value: `${pad(h)}:${pad(m)}` }
}

function toMinutes(value: string): number {
  const [h, m] = value.split(':').map(Number) as [number, number]
  return h * 60 + m
}

function fromMinutes(total: number): string {
  const wrapped = ((total % 1440) + 1440) % 1440
  return `${pad(Math.floor(wrapped / 60))}:${pad(wrapped % 60)}`
}

/** Próximo múltiplo de 15 min ("09:07" → "09:15"; "09:15" fica). Depois de 23:45 fica 23:59 para não virar o dia. */
export function roundUpToQuarter(value: string): string {
  const total = Math.ceil(toMinutes(value) / 15) * 15
  return total >= 1440 ? '23:59' : fromMinutes(total)
}

/** Setas do campo de hora: anda `delta` minutos dando a volta no dia; o passo de 15 alinha ao quarto (09:07 +15 → 09:15). */
export function stepTime(value: string, delta: number): string {
  const total = toMinutes(value)
  if (Math.abs(delta) !== 15 || total % 15 === 0) return fromMinutes(total + delta)
  return fromMinutes(delta > 0 ? Math.ceil(total / 15) * 15 : Math.floor(total / 15) * 15)
}
